import React from "react";
import { observer } from "mobx-react-lite";
import { Box } from "../../design-system";
import { colors } from "../../design-system/colors";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import MainSection from "./components/MainSection";
import MainSectionAlt from "./components/MainSectionAlt";
import FullHybridPage from "./components/FullHybridPage";
import GlitchLandingPage from "./components/GlitchLandingPage";
import EtLabPage from "./components/EtLabPage";
import MarketingLandingPage from "./components/MarketingLandingPage";
import PhoneDemoPage from "./components/PhoneDemoPage";
import PitchDeckPage from "./components/PitchDeckPage";
import StudioConceptPage from "./components/StudioConceptPage";
import HeroEtPage from "./components/HeroEtPage";
import SplitHeroEtPage from "./components/SplitHeroEtPage";
import SpreadsheetTimelinePage from "./components/SpreadsheetTimelinePage";
import TheOnePage from "./components/TheOnePage";
import FlagshipPage from "./components/FlagshipPage";
import PluginPage from "./components/PluginPage";
import SectionLabPage from "./components/SectionLabPage";
import HomeTopNav from "./components/HomeTopNav";
import LayoutWPage from "./components/home3/LayoutWPage";
import LayoutXPage from "./components/home3/LayoutXPage";
import LayoutYPage from "./components/home3/LayoutYPage";
import LayoutZPage from "./components/home3/LayoutZPage";
import { useHomeVariant, useCopySet, LayoutToggle, CopyToggle } from "./components/LayoutToggle";

const SIDEBAR_WIDTH = 260;
const TOP_BAR_HEIGHT = 70;
const MAIN_CONTENT_PADDING = "32px 72px";
const MAIN_CONTENT_MARGIN_RIGHT = 24;
const MAIN_CONTENT_MARGIN_BOTTOM = 24;

const HomePageDashboard: React.FC = () => {
  const [variant, setVariant] = useHomeVariant();
  const [copySet, setCopySet] = useCopySet();

  // Variant A = the original layout, untouched (sidebar + centered hero).
  if (variant === "A") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.background[900],
          display: "flex",
          fontFamily: "Outfit, sans-serif",
        }}
      >
        <Sidebar />

        <Box
          sx={{
            marginLeft: { xs: 0, md: `${SIDEBAR_WIDTH}px` },
            marginTop: `${TOP_BAR_HEIGHT}px`,
            marginRight: { xs: 0, md: `${MAIN_CONTENT_MARGIN_RIGHT}px` },
            marginBottom: `${MAIN_CONTENT_MARGIN_BOTTOM}px`,
            width: { xs: "100%", md: `calc(100% - ${SIDEBAR_WIDTH}px - ${MAIN_CONTENT_MARGIN_RIGHT}px)` },
            minHeight: `calc(100vh - ${TOP_BAR_HEIGHT}px - ${MAIN_CONTENT_MARGIN_BOTTOM}px)`,
            // Clean near-black (not the muddy blue-grey gradient) with a faint cherry
            // glow up top for warmth — makes thumbnails/video pop. Scoped to this page.
            background:
              "radial-gradient(120% 70% at 50% 0%, rgba(230,29,80,0.06), transparent 55%), linear-gradient(180deg, #131218 0%, #0c0b10 100%)",
            borderRadius: { xs: 0, md: "16px" },
            padding: { xs: "16px", md: MAIN_CONTENT_PADDING },
          }}
        >
          <MainSection />
        </Box>

        <TopBar rightOffset={MAIN_CONTENT_MARGIN_RIGHT} />
        <LayoutToggle variant={variant} setVariant={setVariant} />
      </Box>
    );
  }

  // Variant R = the new flagship layout — a real React page (FlagshipPage) that reproduces the
  // approved council-#1 design with the REAL embedded editor + upload box. Its CSS is fully scoped
  // under a unique root class (.ccfp) so it can't leak into — or be affected by — the A–Q layouts
  // or the rest of the app. Purely additive; default home stays "A".
  if (variant === "R") {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#0e0b0d" }}>
        <FlagshipPage />
        <LayoutToggle variant={variant} setVariant={setVariant} />
      </Box>
    );
  }

  // Variant S = the dedicated Premiere-plugin letter ("Controlling Premiere Pro from your Phone!") —
  // a real React page (PluginPage) with the REAL logo + REAL ET examples. CSS scoped under `.ccpp`
  // so it can't affect the rest of the app. Purely additive.
  // U = the section lab: every block from R/S/T in a list you can reorder live.
  // Every layout MUST render the toggle: T and U used to return bare pages, so picking either
  // one hid the letters and stranded you there (the letter is remembered in localStorage, and
  // the only way out was typing ?layout=… by hand). The Section Lab keeps its own panel at the
  // bottom-right, so its toggle sits bottom-left.
  if (variant === "U") {
    return (
      <>
        <SectionLabPage />
        <LayoutToggle variant={variant} setVariant={setVariant} side="left" />
      </>
    );
  }

  // T = R plus the demo video. Same component, one prop — R can't drift away from T.
  if (variant === "T") {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#0e0b0d" }}>
        <FlagshipPage withDemoVideo />
        <LayoutToggle variant={variant} setVariant={setVariant} />
      </Box>
    );
  }

  // W / X / Y = the October 2026 home layouts, three approaches with the phone app as the main
  // ask: W "App first", X "Just type" (prompt-first), Y "The sheet". Self-contained pages (CSS
  // scoped under .h3), also public at /w, /x, /y.
  if (variant === "W" || variant === "X" || variant === "Y" || variant === "Z") {
    return (
      <>
        {variant === "W" ? <LayoutWPage /> : variant === "X" ? <LayoutXPage /> : variant === "Z" ? <LayoutZPage /> : <LayoutYPage />}
        <LayoutToggle variant={variant} setVariant={setVariant} />
      </>
    );
  }

  if (variant === "S") {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#0e0b0d" }}>
        <PluginPage />
        <LayoutToggle variant={variant} setVariant={setVariant} />
      </Box>
    );
  }

  // Variants B–F = experiment layouts: no sidebar (the teacher called it distracting), a clean
  // top nav (logo + My Videos + Learning Center + Upload + account), and a full-width hero.
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: colors.background[900],
        fontFamily: "Outfit, sans-serif",
        background:
          "radial-gradient(120% 60% at 50% 0%, rgba(230,29,80,0.07), transparent 55%), linear-gradient(180deg, #131218 0%, #0c0b10 100%)",
      }}
    >
      <HomeTopNav />
      <Box sx={{ marginTop: "64px", minHeight: "calc(100vh - 64px)" }}>
        {variant === "Q" ? (
          <TheOnePage />
        ) : variant === "P" ? (
          <SpreadsheetTimelinePage />
        ) : variant === "O" ? (
          <SplitHeroEtPage />
        ) : variant === "N" ? (
          <HeroEtPage />
        ) : variant === "M" ? (
          <StudioConceptPage />
        ) : variant === "L" ? (
          <PitchDeckPage />
        ) : variant === "K" ? (
          <PhoneDemoPage />
        ) : variant === "J" ? (
          <MarketingLandingPage />
        ) : variant === "I" ? (
          <EtLabPage />
        ) : variant === "H" ? (
          <GlitchLandingPage
            onTrySample={() => { try { document.getElementById("cc-glitch-upload")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { /* ignore */ } }}
          />
        ) : variant === "G" ? (
          <FullHybridPage
            copySet={copySet}
            onTrySample={() => { try { document.getElementById("cc-upload")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { /* ignore */ } }}
          />
        ) : (
          <Box sx={{ padding: { xs: "16px", md: variant === "F" ? "24px" : "24px 48px 48px" } }}>
            <MainSectionAlt variant={variant} />
          </Box>
        )}
      </Box>

      <LayoutToggle variant={variant} setVariant={setVariant} />
      {variant === "G" && <CopyToggle copySet={copySet} setCopySet={setCopySet} />}
    </Box>
  );
};

export default observer(HomePageDashboard);
